/**
 * VOIHU — Google Analytics 4 with privacy-safe Consent Mode v2.
 *
 * Contract:
 *   • Consent Mode defaults (all signals = "denied") are set inline in
 *     public/index.html BEFORE this module executes. This module never
 *     downgrades that default; it only calls `gtag('consent','update',…)`
 *     with `analytics_storage: 'granted'` after an explicit user opt-in
 *     via the consent banner.
 *   • Advertising signals (ad_storage / ad_user_data / ad_personalization)
 *     remain "denied" forever — VOIHU does not deploy Google Ads,
 *     remarketing or advertising pixels.
 *   • gtag.js is injected on-demand exactly once, only after consent is
 *     granted. On subsequent page loads the stored consent is applied
 *     synchronously so returning users don't see the banner again.
 *   • SPA route changes call `trackPageView()` from App.js. gtag `config`
 *     is created with `send_page_view: false` so the config call itself
 *     never emits a page_view — every page_view (including the first)
 *     comes from an explicit `event page_view` here, ensuring no
 *     duplicates.
 *
 * Data minimisation: we send only the default GA4 event payload —
 * page_path / page_location / page_title — and never attach email,
 * user id, MESSAGE / VOICE text, coordinates, referral identities,
 * auth tokens, payment info or any other personal data.
 */

export const GA_MEASUREMENT_ID = "G-9PG849Y0PE";
export const CONSENT_STORAGE_KEY = "voihu_analytics_consent";
export const CONSENT_EVENT = "voihu:open-consent";

let gtagScriptInjected = false;
let gaConfigured = false;

const safeGtag = (...args) => {
  if (typeof window === "undefined") return;
  const fn = window.gtag;
  if (typeof fn === "function") fn(...args);
};

const injectGtagScript = () => {
  if (gtagScriptInjected || typeof document === "undefined") return;
  gtagScriptInjected = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(s);
};

const configureOnce = () => {
  if (gaConfigured) return;
  gaConfigured = true;
  safeGtag("js", new Date());
  // send_page_view: false — every page_view is emitted explicitly by
  // trackPageView() so client-side navigations don't produce duplicates.
  // anonymize_ip: true is a defensive default even though GA4 already
  // truncates IPs server-side.
  safeGtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });
};

/** Returns "granted" / "denied" / null (undecided). */
export const getAnalyticsConsent = () => {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch (_) {
    return null;
  }
};

/**
 * Persist the user's decision and update Google Consent Mode.
 * `value` must be "granted" or "denied".
 * Advertising signals ALWAYS stay denied regardless of the analytics choice.
 */
export const setAnalyticsConsent = (value) => {
  if (value !== "granted" && value !== "denied") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch (_) { /* private-mode fallthrough */ }

  safeGtag("consent", "update", {
    analytics_storage: value,
    // Advertising surfaces stay denied — not part of this task's scope.
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  if (value === "granted") {
    injectGtagScript();
    configureOnce();
    // Fire the current page_view immediately so first-visit acceptance
    // still records the landing page even though the config no longer
    // does it automatically.
    trackPageView();
  }
};

/**
 * Emit a GA4 page_view for the current SPA location. Silently no-ops
 * when consent has not been granted. Called by App.js on every route
 * change (and once on mount).
 */
export const trackPageView = (pathOverride) => {
  if (getAnalyticsConsent() !== "granted") return;
  if (typeof window === "undefined") return;
  // Some paths pass through with search strings intact so GA4 sees UTM
  // params (utm_source / utm_medium / utm_campaign / utm_content).
  const path = pathOverride ||
               (window.location.pathname + window.location.search);
  configureOnce();
  safeGtag("event", "page_view", {
    page_path:     path,
    page_location: window.location.href,
    page_title:    typeof document !== "undefined" ? document.title : undefined,
  });
};

/**
 * On app boot: if the visitor previously opted in, wire GA4 up so the
 * first route change is measured. If undecided or previously rejected,
 * do nothing — the banner will handle it.
 */
export const initAnalyticsFromStoredConsent = () => {
  const stored = getAnalyticsConsent();
  if (stored === "granted") {
    safeGtag("consent", "update", { analytics_storage: "granted" });
    injectGtagScript();
    configureOnce();
  }
};

/**
 * Small helper the Cookies page can use to reopen the banner so users
 * can change their mind. Fires a DOM custom event; ConsentBanner
 * listens for it and re-mounts itself.
 */
export const openConsentBanner = () => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(CONSENT_EVENT));
  } catch (_) { /* older browsers */ }
};
